import { IconButton, Paper, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditableTable from "../../../../Controllers/EditableTable";
import { useDispatch, useSelector } from "react-redux";
import { proposalSelector } from "../../../../store/proposalSlice";
import {
  applicantSelector,
  budgetCodesSelector,
  setBudgetCodes,
} from "../../../../store/applicantsSlice";
import { useLoading } from "../../../../providers/loadingProvider";
import service from "../../helpers/GifService";
import { Box } from "@mui/system";
import InfoDialog from "./InfoDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  selectTableCell: {
    minWidth: 150,
  },
  textError: {
    color: "red",
  },
  editButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.yellow.hover,
    },
    color: theme.palette.gif.yellow.dark,
    backgroundColor: theme.palette.gif.yellow.color,
  },
  saveButton: {
    "&:hover": {
      backgroundColor: theme.palette.gif.blue.hover,
    },
    color: theme.palette.gif.blue.color,
    backgroundColor: theme.palette.gif.blue.background,
  },
  infoIcon: {
    marginLeft: 5,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  totalTypography: {
    fontSize: 16,
    fontWeight: 600,
  },
  totalRow: {
    backgroundColor: "#D0D0D0",
  },
}));

export default function BudgetScreen(props) {
  const { buildTableConfig, title, releventCodes, infoField, caption } = props;
  const classes = useStyles();
  const loading = useLoading();
  const dispatch = useDispatch();

  const proposal = useSelector(proposalSelector);
  const budgetCodes = useSelector(budgetCodesSelector);
  const applicant = useSelector(applicantSelector);

  const [codes, setCodes] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [infoContent, setInfoContent] = useState("");
  const tableConfig = useMemo(
    () => buildTableConfig(proposal.numberOfYears, codes),
    [codes, buildTableConfig, proposal.numberOfYears]
  );

  const [budgetClauses, setBudgetClauses] = useState([]);

  const getBudgetClauses = useCallback(async () => {
    const loadingId = loading.addLoading("loading budgets for proposal");
    var budgetClauses = await service.getBudgetClausesByProposalId({
      proposalId: proposal.id,
    });
    budgetClauses = budgetClauses.filter((bc) =>
      releventCodes.includes(bc.budgetTypeInt)
    );
    setBudgetClauses(budgetClauses);
    loading.removeLoading(loadingId);
  }, [loading, proposal.id, releventCodes]);

  const getBudgetCodes = useCallback(async () => {
    let codes = budgetCodes;
    if (!codes) {
      const loadingId = loading.addLoading("Loading Budget Codes");
      codes = await service.GetBudgetCodes();
      dispatch(setBudgetCodes(codes));
      loading.removeLoading(loadingId);
    }
    return codes.filter((bc) => releventCodes.includes(bc.value));
  }, [budgetCodes, loading, dispatch, releventCodes]);

  const budgetCreated = useCallback(
    async (row) => {
      const loadingId = loading.addLoading("creating budget");
      if (!row.applicantGranteeId) {
        row.applicantGranteeId = applicant.id;
      }
      row.roleInProgectSalary = "PI";
      if (releventCodes.length === 1) row.budgetTypeInt = releventCodes[0];
      service
        .createBudgetClause({
          budgetClause: row,
        })
        .then(() => {
          loading.removeLoading(loadingId);
          getBudgetClauses();
        });
    },
    [applicant.id, getBudgetClauses, loading, releventCodes]
  );

  const budgetUpdated = useCallback(
    (row) => {
      const loadingId = loading.addLoading("updating budget");
      if (releventCodes.length === 1) row.budgetTypeInt = releventCodes[0];
      service
        .updateBudgetClause({
          budgetClause: row,
        })

        .then(() => {
          loading.removeLoading(loadingId);
          getBudgetClauses();
        });
    },
    [getBudgetClauses, loading, releventCodes]
  );

  const budgetDeleted = (budgetClauseId) => {
    const loadingId = loading.addLoading("Deleting");
    service.DeactiveBudgetClause({ budgetClauseId }).then(() => {
      loading.removeLoading(loadingId);
      getBudgetClauses();
    });
  };

  const showInfoDialog = useCallback(async () => {
    var pageInfo = await service.getProgramInfoByProgramId({
      infoField,
      programType: proposal.programType,
    });
    setInfoContent(pageInfo);
    setShowInfo(true);
  }, [infoField, proposal]);

  useEffect(() => {
    const getData = async () => {
      const loadingId = loading.addLoading("loading budgets");
      getBudgetClauses();
      var budgetCodes = await getBudgetCodes();
      setCodes(budgetCodes.map((c) => ({ ...c, id: c.value })));
      loading.removeLoading(loadingId);
      if (releventCodes.length === 0)
        throw new Error("releventCodes are not defined");
    };
    getData();
  }, [getBudgetClauses, getBudgetCodes, loading, releventCodes.length]);

  return (
    <>
      <InfoDialog
        open={showInfo}
        onClose={() => setShowInfo(false)}
        content={infoContent}
      ></InfoDialog>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="subtitle2">{title}</Typography>
        <IconButton onClick={showInfoDialog}>
          <InfoOutlinedIcon color="primary" />
        </IconButton>
      </Box>
      <Box>
        <Typography variant="caption">{caption}</Typography>
      </Box>
      <Paper className={classes.root} elevation={0}>
        <EditableTable
          tableConfig={tableConfig}
          tableData={budgetClauses}
          rowCreated={budgetCreated}
          rowUpdated={budgetUpdated}
          rowDeleted={budgetDeleted}
          totalRow={true}
        />
      </Paper>
    </>
  );
}
